import { Injectable } from '@angular/core';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MemberModel, MemberResponse } from '../models/member.model';
import { map, tap } from 'rxjs/operators';
import { MemberOrganizationInfoResponse } from '../models/member-organization-info.model';
import { MemberOrganizationInfoModel } from '../../core/models/member-organization-info.model';

@Injectable({
  providedIn: 'root',
})
export class ManageMembersService {
  apiEndpointBaseUrl: string;

  constructor(private http: HttpClient) {
    this.apiEndpointBaseUrl = environment.apiEndpointBaseUrl;
  }

  public getActiveMembers(organization: string): Observable<MemberModel[]> {
    return this.getMembers(organization).pipe(
        map(members => this.filterActiveMembers(members))
    );
  }

  public getMembers(organization: string): Observable<MemberModel[]> {
    const httpOptions = {
      headers: new HttpHeaders({
        organization: organization,
      }),
    };
    return this.http
      .get<MemberResponse>(this.apiEndpointBaseUrl + 'search-members', httpOptions)
      .pipe(
        map((res) => this.parseResponseToMembers(res)),
        tap((members) => this.sortMembers(members)),
      );
  }
  
  public getMemberOrganizationInfo(user: string): Observable<MemberOrganizationInfoModel[]> {
    const httpOptions = {
      headers: new HttpHeaders({
        uid: user,
      }),
    };
    return this.http.get<MemberOrganizationInfoModel[]>(
      this.apiEndpointBaseUrl + 'member-organization-info',
      httpOptions
    );
  }

  public deleteMember(organization: string, user: string, crmId): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        bpid: organization,
        uid: user,
        crmId: crmId
      }),
    };
    return this.http.delete(this.apiEndpointBaseUrl + 'remove-members', httpOptions);
  }

  public modifyMember(organization: string, user: string, status: string): Observable<any> {
    const body = {};
    const httpOptions = {
      headers: new HttpHeaders({
        bpid: organization,
        uid: user,
        status: status,
      }),
    };
    return this.http.patch(
      this.apiEndpointBaseUrl + 'activate-deactivate-member',
      body,
      httpOptions
    );
  }

  public updateRoles(
    organization: string,
    user: string,
    roleNames: string,
    status: string,
    extraOperation: string = '',
    extraRoleName: string = ''
  ) {
    if (roleNames.length === 0) {
      roleNames = 'DEFAULT';
    }
    const httpOptions = {
      headers: new HttpHeaders({
        bpid: organization,
        uid: user,
        roleNames: roleNames,
        status: status,
        extraOperation: extraOperation,
        extraRoleName: extraRoleName,
      }),
    };
    return this.http
      .post<MemberResponse>(this.apiEndpointBaseUrl + 'update-roles', {}, httpOptions)
      .pipe(
        map((res) => this.parseResponseToMembers(res)),
        tap((members) => this.sortMembers(members))
      );
  }

  public parseAndUpdateOrganizationInfo(
    data: any,
    memberOrganizationInfo: MemberOrganizationInfoModel,
    organizationBPID: string
  ): MemberOrganizationInfoModel {
    const results = data?.results;
    const firstResult = results?.length > 0 ? results[0] : null;
    const organizations: MemberOrganizationInfoResponse[] = firstResult?.groups?.organizations;

    if (organizations) {
      const target = organizations.find((org) => org.bpid === organizationBPID);
      if (!target) return;

      return {
        ...memberOrganizationInfo,
        bpid: target.bpid,
        orgId: target.orgId,
        orgName: target.orgName,
        department: target.department,
        job: target.job,
        status: target.status,
        complete: true,
        roles: target.roles?.filter((role) => role) ?? [],
      };
    }
    return memberOrganizationInfo;
  }

  private parseResponseToMembers(response: MemberResponse): MemberModel[] {
    return response.results.map((it) => new MemberModel(it));
  }

  private sortMembers(members: MemberModel[]): void {
    members.sort((a, b) => a.email.localeCompare(b.email));
  }  

  /* The current logic to retrieve active members is based on 
    having an existing username*/
  private filterActiveMembers(members: MemberModel[]): MemberModel[] {
    return members.filter(a => a.username != undefined && a.username != null && a.username != '');
  }
} 